import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { ImageCacheProvider } from './contexts/ImageCacheContext';
import { ToastProvider } from './contexts/ToastContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { SellerRoute } from './components/auth/SellerRoute';
import { initPerformanceMonitoring } from './utils/performanceMonitoring';
import { initRoutePrefetching } from './utils/routePrefetcher';
import RouteChangeTracker from './components/performance/RouteChangeTracker';
import ScrollToTop from './components/performance/ScrollToTop';
import SellerOrdersPage from './pages/SellerOrdersPage';
import BuyerOrdersPage from './pages/BuyerOrdersPage';
import TestSellerFlow from './pages/TestSellerFlow';
import TestUserUpdate from './pages/TestUserUpdate';

// Lazy load page components
const HomePage = lazy(() => import('./pages/HomePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SellerStorefrontPage = lazy(() => import('./pages/SellerStorefrontPage'));
const SellerCustomizationPage = lazy(() => import('./pages/SellerCustomizationPage'));
const BuyerSignupPage = lazy(() => import('./pages/BuyerSignupPage'));
const UnifiedSignupPage = lazy(() => import('./pages/UnifiedSignupPage'));
const SellerSignupPage = lazy(() => import('./pages/SellerSignupPage'));
const ListingsPage = lazy(() => import('./pages/ListingsPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const EnhancedCheckoutPage = lazy(() => import('./components/checkout/EnhancedCheckoutPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const OrderHistoryPage = lazy(() => import('./pages/OrderHistoryPage'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
const SellerShippingPage = lazy(() => import('./pages/SellerShippingPage'));
const CreateListingPage = lazy(() => import('./pages/CreateListingPage'));
const ManageListingsPage = lazy(() => import('./pages/ManageListingsPage'));
const SubscriptionTiers = lazy(() => import('./pages/Subscription-Tiers'));
const SubscriptionManagementPage = lazy(() => import('./pages/SubscriptionManagementPage'));
const InboxPage = lazy(() => import('./pages/InboxPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const SellersDirectoryPage = lazy(() => import('./pages/SellersDirectoryPage'));
const ListingDetailPage = lazy(() => import('./pages/ListingDetailPage'));
const SellerRegistrationComplete = lazy(() => import('./pages/SellerRegistrationComplete'));
const NewBecomeSellerPage = lazy(() => import('./pages/NewBecomeSellerPage'));
const TestForceSeller = lazy(() => import('./pages/TestForceSeller'));
const OffersPage = lazy(() => import('./pages/OffersPage'));
const StripeConnectPage = lazy(() => import('./pages/StripeConnectPage'));
const SellerAnalyticsPage = lazy(() => import('./pages/SellerAnalyticsPage'));
const SellerPromoCodesPage = lazy(() => import('./pages/SellerPromoCodesPage'));

// Footer pages
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const ShippingInfoPage = lazy(() => import('./pages/ShippingInfoPage'));
const SellLandingPage = lazy(() => import('./pages/SellLandingPage'));
const BuyLandingPage = lazy(() => import('./pages/BuyLandingPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));

// Legal pages
const CopyrightPage = lazy(() => import('./pages/CopyrightPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));

// Admin pages
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const OrderManagement = lazy(() => import('./pages/OrderManagement'));
const ListingModeration = lazy(() => import('./pages/ListingModeration'));
const SubscriptionsPayments = lazy(() => import('./pages/SubscriptionsPayments'));
const SupportTickets = lazy(() => import('./pages/SupportTickets'));
const ReportsAnalytics = lazy(() => import('./pages/ReportsAnalytics'));
const SystemLogs = lazy(() => import('./pages/SystemLogs'));
const AdminPromoCodesPage = lazy(() => import('./pages/AdminPromoCodesPage'));
const SalesTaxManagement = lazy(() => import('./pages/admin/SalesTaxManagement'));

// Password management pages
const ForcePasswordChangePage = lazy(() => import('./pages/ForcePasswordChangePage'));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const App = () => {
  // Initialize performance monitoring and route prefetching
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      initPerformanceMonitoring({
        sampleRate: 0.1,
        debug: false
      });
    }
    initRoutePrefetching();
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <ImageCacheProvider>
            <ToastProvider>
              <Router>
                <ScrollToTop />
                <RouteChangeTracker />
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <main className="flex-grow pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <Switch>
                      {/* Public Routes */}
                      <Route path="/" exact component={HomePage} />
                      <Route path="/login" component={LoginPage} />
                      <Route path="/force-password-change" component={ForcePasswordChangePage} />
                      <Route path="/signup" exact component={UnifiedSignupPage} />
                      <Route path="/signup/seller" component={SellerSignupPage} />
                      <Route path="/signup/buyer" component={BuyerSignupPage} />
                      <Route path="/subscription-tiers" component={SubscriptionTiers} />
                      <Route path="/search" component={SearchResultsPage} />
                      <Route path="/sellers" component={SellersDirectoryPage} />
                      <Route path="/sell" component={SellLandingPage} />
                      <Route path="/buy" component={BuyLandingPage} />
                      <Route path="/how-it-works" component={HowItWorksPage} />
                      <Route path="/listing/:id" component={ListingDetailPage} />
                      <Route path="/storefront/:storeId" component={SellerStorefrontPage} />
                      <Route path="/cart" component={CartPage} />
                      <Route path="/checkout" component={EnhancedCheckoutPage} />
                      <Route path="/checkout-legacy" component={CheckoutPage} />
                      <Route path="/checkout-success" component={CheckoutSuccessPage} />
                      <Route path="/order-confirmation" component={OrderConfirmationPage} />
                      <Route path="/listings" component={ListingsPage} />
                      
                      {/* Footer Pages */}
                      <Route path="/faq" component={FAQPage} />
                      <Route path="/contact" component={ContactUsPage} />
                      <Route path="/shipping" component={ShippingInfoPage} />
                      
                      {/* Legal Pages */}
                      <Route path="/copyright" component={CopyrightPage} />
                      <Route path="/terms" component={TermsPage} />
                      <Route path="/privacy" component={PrivacyPage} />

                      {/* Protected Routes (any authenticated user) */}
                      <Route
                        path="/profile"
                        render={() => (
                          <ProtectedRoute>
                            <ProfilePage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path="/order-history"
                        render={() => (
                          <ProtectedRoute>
                            <OrderHistoryPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path="/orders"
                        render={() => (
                          <ProtectedRoute>
                            <BuyerOrdersPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path="/offers"
                        render={() => (
                          <ProtectedRoute>
                            <OffersPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path="/checkout"
                        render={() => (
                          <ProtectedRoute>
                            <CheckoutPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path="/become-seller"
                        exact
                        render={() => (
                          <ProtectedRoute>
                            <NewBecomeSellerPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path="/become-seller/new"
                        render={() => (
                          <ProtectedRoute>
                            <NewBecomeSellerPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path="/become-seller-direct"
                        render={() => (
                          <ProtectedRoute>
                            <NewBecomeSellerPage directMode={true} />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path="/become-seller/complete"
                        render={() => (
                          <ProtectedRoute>
                            <SellerRegistrationComplete />
                          </ProtectedRoute>
                        )}
                      />
                      <Route path="/inbox" component={InboxPage} />
                      <Route path="/test-force-seller" component={TestForceSeller} />
                      <Route path="/test-seller-flow" component={TestSellerFlow} />
                      <Route path="/test-user-update" component={TestUserUpdate} />

                      {/* Seller Routes */}
                      <SellerRoute path="/seller/dashboard" component={SellerDashboard} />
                      <SellerRoute path="/seller/create-listing" component={CreateListingPage} />
                      <SellerRoute path="/seller/customize" component={SellerCustomizationPage} />
                      <SellerRoute path="/seller/storefront" component={SellerStorefrontPage} />
                      <SellerRoute path="/seller/shipping" component={SellerShippingPage} />
                      <SellerRoute path="/seller/promo-codes" component={SellerPromoCodesPage} />
                      <SellerRoute path="/seller/stripe-connect" component={StripeConnectPage} />
                      <SellerRoute path="/analytics" component={SellerAnalyticsPage} />
                      <Route path="/seller/subscription" component={SubscriptionManagementPage} />
                      <Route path="/subscription-management" component={SubscriptionManagementPage} />
                      <SellerRoute path="/manage-listings" component={ManageListingsPage} />
                      <Route
                        path="/seller/orders"
                        render={() => (
                          <ProtectedRoute roles={['seller']}>
                            <SellerOrdersPage />
                          </ProtectedRoute>
                        )}
                      />

                      {/* Admin Routes */}
                      <AdminRoute path="/admin" exact component={AdminDashboard} />
                      <AdminRoute path="/admin/users" component={UserManagement} />
                      <AdminRoute path="/admin/orders" component={OrderManagement} />
                      <AdminRoute path="/admin/listings" component={ListingModeration} />
                      <AdminRoute path="/admin/subscriptions" component={SubscriptionsPayments} />
                      <AdminRoute path="/admin/tickets" component={SupportTickets} />
                                              <AdminRoute path="/admin/reports" component={ReportsAnalytics} />
                        <AdminRoute path="/admin/promo-codes" component={AdminPromoCodesPage} />
                        <AdminRoute path="/admin/logs" component={SystemLogs} />
                        <AdminRoute path="/admin/sales-tax" component={SalesTaxManagement} />

                      {/* Catch All Route */}
                      <Route path="*" component={() => <h1>404 Not Found</h1>} />
                    </Switch>
                  </Suspense>
                </main>
                
                {/* Development Banner - Bottom */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="bg-red-500 text-white text-xs font-mono text-center py-1 px-2 whitespace-nowrap overflow-hidden">
                    <span style={{
                      display: 'inline-block',
                      width: '100%',
                      whiteSpace: 'nowrap',
                      letterSpacing: '2px',
                      fontWeight: 600
                    }}>
                      {Array(50).fill('Dev/Dev/Dev/Dev ').join('')}
                    </span>
                  </div>
                )}
                
                <Footer />
              </div>
            </Router>
            </ToastProvider>
          </ImageCacheProvider>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;